/**
 * SoutAnketaView — полная read-only анкета СОУТ для команды (замечание менеджера F2-14):
 * краткий блок в карточке заказа команды заменён на полный просмотр всей анкеты клиента в
 * 5 вкладках, в порядке разделов формы заказа услуги («Данные о компании» → «Комиссия» →
 * «Ответственный» → «Согласие на обработку ПД» → «Рабочие места»). Вкладки — НЕ шаги
 * (просмотр не требует последовательного прохождения), примитив Tabs (ДС, F2-04).
 *
 * Данные — из СНИМКА заказа (`SoutAnketa`, GET /api/team/orders/{id}/sout), не из текущего
 * профиля/структуры: команда видит анкету ровно такой, какой её сдал клиент (инвариант
 * «фотография»). Порядок рабочих мест = порядку `anketa.lines` (как отдаёт сервер, по заказу).
 *
 * Пустые необязательные поля — «—».
 *
 * centrika-docs!89 (вкладка «Рабочие места»): вместо списка сворачиваемых блоков — кнопка
 * «Скачать в Excel» перед таблицей и таблица рабочих мест в порядке анкеты; каждое место —
 * отдельная строка (подразделение, адрес, должность, ФИО, СНИЛС) с раскрываемой под ней полной
 * информацией. Строки раскрываются независимо, значения пяти колонок при раскрытии остаются
 * видимыми (полная информация рисуется отдельной строкой НИЖЕ). Состав раскрытой карточки
 * (`LineDetail`) требование не меняет.
 */
import { useState, type ReactNode } from 'react'
import { Download } from 'lucide-react'
import { teamApi, type SoutAnketa, type SoutCompensations, type SoutLine, type SoutRmType } from '@/lib/api'
import { downloadBlob } from '@/lib/download'
import { formatWorkHours } from '@/lib/work-hours'
import {
  SOUT_WORKPLACES_EXPORT_ERROR,
  formatSnils,
  resolveScheduleDisplay,
  soutWorkplacesFileName,
} from '@/lib/sout'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/design-system/components/primitives/tabs'
import { Badge } from '@/design-system/components/primitives/badge'
import { Button } from '@/design-system/components/primitives/button'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'

const EMPTY = '—'

function orEmpty(v?: string | null): string {
  const s = (v ?? '').trim()
  return s || EMPTY
}

function orEmptyNum(n: number): string {
  return n > 0 ? String(n) : EMPTY
}

function formatDate(iso?: string): string {
  if (!iso) return EMPTY
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const RM_TYPE_LABEL: Record<SoutRmType, string> = { D: 'Действующее', V: 'Вакансия' }

function genderLabel(g: string): string {
  if (g === 'm') return 'муж.'
  if (g === 'f') return 'жен.'
  return EMPTY
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start gap-3 border-b border-line py-2.5 last:border-b-0">
      <div className="flex-1 text-txt-secondary">{label}</div>
      <span className="text-right font-medium text-txt">{value}</span>
    </div>
  )
}

function SectionTitle({ children }: { children: ReactNode }) {
  return <p className="mb-1 mt-4 text-13 font-semibold text-txt first:mt-0">{children}</p>
}

// --- Вкладка 1: «Данные о компании» (снимок организации, §3.4) ---

function OrganizationTab({ anketa }: { anketa: SoutAnketa }) {
  const o = anketa.organization
  if (!o) {
    return <p className="py-2 text-txt-secondary">Данные об организации отсутствуют.</p>
  }
  return (
    <div>
      <Row label="Полное наименование" value={orEmpty(o.nameFull)} />
      <Row label="Сокращённое наименование" value={orEmpty(o.nameShort)} />
      <Row label="ИНН" value={orEmpty(o.inn)} />
      <Row label="КПП" value={orEmpty(o.kpp)} />
      <Row label="ОГРН" value={orEmpty(o.ogrn)} />
      <Row label="ОКПО" value={orEmpty(o.okpo)} />
      <Row label="ОКТМО" value={orEmpty(o.oktmo)} />
      <Row label="ОКВЭД" value={orEmpty(o.okved)} />
      <Row label="ОКОГУ" value={orEmpty(o.okogu)} />
      <Row label="Юридический адрес" value={orEmpty(o.legalAddress)} />
      <Row label="Email для документов" value={orEmpty(o.email)} />
      <Row label="Телефон" value={orEmpty(o.phone)} />
      <Row label="Руководитель" value={orEmpty(o.directorName)} />
      <Row label="Должность руководителя" value={orEmpty(o.directorPosition)} />
    </div>
  )
}

// --- Вкладка 2: «Комиссия» ---

function CommissionTab({ anketa }: { anketa: SoutAnketa }) {
  const members = anketa.commission
  if (members.length === 0) {
    return <p className="py-2 text-txt-secondary">Комиссия не заполнена.</p>
  }
  return (
    <div>
      {members.map((m, i) => (
        <div
          key={i}
          className="flex items-center justify-between gap-3 border-b border-line py-2.5 last:border-b-0"
        >
          <div className="min-w-0">
            <div className="font-medium text-txt">{orEmpty(m.fullName)}</div>
            <div className="text-12 text-txt-secondary">{orEmpty(m.position)}</div>
          </div>
          {m.isChair ? <Badge status="success">Председатель</Badge> : null}
        </div>
      ))}
    </div>
  )
}

// --- Вкладка 3: «Ответственный» ---

function ResponsibleTab({ anketa }: { anketa: SoutAnketa }) {
  const r = anketa.responsible
  return (
    <div>
      <Row label="ФИО" value={orEmpty(r.fullName)} />
      <Row label="Должность" value={orEmpty(r.position)} />
      <Row label="Телефон" value={orEmpty(r.phone)} />
      <Row label="Email" value={orEmpty(r.email)} />
    </div>
  )
}

// --- Вкладка 4: «Согласие на обработку персональных данных» ---

function PdConsentTab({ anketa }: { anketa: SoutAnketa }) {
  return (
    <div>
      <Row label="Согласие на обработку персональных данных" value={anketa.pdConsent ? 'Да' : 'Нет'} />
      <Row label="Дата согласия" value={formatDate(anketa.pdConsentAt)} />
    </div>
  )
}

// --- Вкладка 5: «Рабочие места» (аккордеон по местам) ---

function CompensationsList({ c }: { c: SoutCompensations }) {
  const items: Array<[string, boolean]> = [
    ['Доплаты', c.extraPay],
    ['Дополнительный отпуск', c.extraVacation],
    ['Сокращённая рабочая неделя', c.shortWeek],
    ['Выдача молока (или равноценных продуктов)', c.milk],
    ['Досрочная льготная пенсия по старости', c.earlyPension],
    ['Медосмотры', c.medical],
  ]
  return (
    <div>
      {items.map(([label, value]) => (
        <Row key={label} label={label} value={value ? 'Да' : 'Нет'} />
      ))}
    </div>
  )
}

function LineDetail({ line }: { line: SoutLine }) {
  const p = line.person
  const c = line.conditions
  const schedule = resolveScheduleDisplay(c)
  return (
    <div className="flex flex-col">
      <SectionTitle>Рабочее место</SectionTitle>
      <Row label="Подразделение" value={orEmpty(c.departmentName)} />
      <Row label="Фактический адрес" value={orEmpty(c.actualAddress)} />
      <Row label="Должность" value={orEmpty(c.position)} />
      <Row label="Тип рабочего места" value={RM_TYPE_LABEL[c.rmType]} />

      <SectionTitle>Сотрудник</SectionTitle>
      <Row label="ФИО" value={orEmpty(p.fullName)} />
      <Row label="СНИЛС" value={p.snils ? formatSnils(p.snils) : EMPTY} />
      <Row label="Пол" value={genderLabel(p.gender)} />
      <Row label="Дата рождения" value={formatDate(p.birthDate)} />
      <Row label="Несовершеннолетний" value={p.isMinor ? 'Да' : 'Нет'} />
      {/* Признак инвалидности НЕ выводится (centrika-docs!39): специальная категория
          персданных скрыта из интерфейса, в данных заказа он сохраняется как был. */}

      <SectionTitle>Режим работы</SectionTitle>
      <Row label="Часы смены" value={formatWorkHours(c.shiftHours) || EMPTY} />
      <Row label="Часов в неделю" value={formatWorkHours(c.weekHours) || EMPTY} />
      <Row label="График работы" value={orEmpty(schedule.schedule)} />
      {schedule.schedule === 'Иной' ? (
        <Row label="Описание графика" value={orEmpty(schedule.scheduleOther)} />
      ) : null}

      {/* Без строки «Проводилась ранее»: чекбокс убран из формы (D8), у новых мест признак
          всегда false и противоречил выбранному классу (sout-export-previous-class). Сведения
          о прошлой СОУТ — только класс, где «Не проводилось» — значение справочника. */}
      <SectionTitle>Прошлая спецоценка</SectionTitle>
      <Row label="Индивидуальный № РМ" value={orEmpty(c.rmNumber)} />
      <Row label="Класс по прошлой спецоценке" value={orEmpty(c.previousClass)} />

      <SectionTitle>Компенсации только за вредные условия труда</SectionTitle>
      <CompensationsList c={c.compensations} />

      <SectionTitle>Оборудование</SectionTitle>
      <p className="whitespace-pre-wrap py-1 text-txt">{orEmpty(c.equipment)}</p>

      <SectionTitle>Материалы</SectionTitle>
      <p className="whitespace-pre-wrap py-1 text-txt">{orEmpty(c.materials)}</p>

      <SectionTitle>Служебный транспорт</SectionTitle>
      {c.transport.length === 0 ? (
        <p className="py-1 text-txt-secondary">{EMPTY}</p>
      ) : (
        c.transport.map((t, i) => (
          <div key={i} className="mb-2 rounded-8 border border-line p-3 last:mb-0">
            <Row label="Марка" value={orEmpty(t.brand)} />
            <Row label="Гос. номер" value={orEmpty(t.plate)} />
            <Row label="Год выпуска" value={orEmptyNum(t.year)} />
            <Row label="Разрешённая масса, кг" value={orEmptyNum(t.massLaden)} />
            <Row label="Масса без нагрузки, кг" value={orEmptyNum(t.massUnladen)} />
          </div>
        ))
      )}

      <SectionTitle>Несчастные случаи</SectionTitle>
      {c.accidents.length === 0 ? (
        <p className="py-1 text-txt-secondary">{EMPTY}</p>
      ) : (
        c.accidents.map((a, i) => (
          <div key={i} className="mb-2 rounded-8 border border-line p-3 last:mb-0">
            <Row label="Тяжесть" value={orEmpty(a.severity)} />
            <Row label="Описание" value={orEmpty(a.description)} />
            <Row label="Дата" value={formatDate(a.date)} />
          </div>
        ))
      )}
    </div>
  )
}

/**
 * Кнопка «Скачать в Excel» (centrika-docs!89) — перед таблицей рабочих мест, администратору и
 * сотруднику Центрики (вся эта страница — командная). Файл формирует СЕРВЕР по сохранённым
 * данным анкеты на момент формирования, независимо от того, какие строки раскрыты. Пока файл
 * формируется, кнопка недоступна и показывает «Формирование файла…»; рабочих мест нет — кнопка
 * недоступна; при неудаче рядом с кнопкой появляется сообщение, а кнопка снова доступна.
 */
function WorkplacesExportButton({
  orderId,
  orderNumber,
  disabled,
}: {
  orderId: string
  orderNumber: string
  disabled: boolean
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function download() {
    setError(null)
    setBusy(true)
    try {
      downloadBlob(
        soutWorkplacesFileName(orderNumber),
        await teamApi.sout.downloadWorkplaces(orderId),
      )
    } catch {
      setError(SOUT_WORKPLACES_EXPORT_ERROR)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mb-3 flex flex-wrap items-center gap-3">
      <Button
        size="sm"
        variant="secondary"
        disabled={busy || disabled}
        onClick={() => void download()}
        leftIcon={<Download className="size-4 stroke-current" aria-hidden />}
      >
        {busy ? 'Формирование файла…' : 'Скачать в Excel'}
      </Button>
      {error ? <span className="text-12 text-red">{error}</span> : null}
    </div>
  )
}

/**
 * Строка таблицы рабочих мест (centrika-docs!89): пять колонок и раскрываемая под строкой
 * полная информация. Каждая строка раскрывается и сворачивается независимо от остальных;
 * значения пяти колонок остаются видимыми при раскрытии (полная информация — отдельная
 * строка НИЖЕ). Состав раскрытой карточки — прежний `LineDetail`.
 */
function LineTableRow({ line, index }: { line: SoutLine; index: number }) {
  const [open, setOpen] = useState(false)
  const c = line.conditions
  const p = line.person
  return (
    <>
      <DataTableRow
        clickable
        aria-expanded={open}
        aria-label={`Рабочее место №${index}: ${orEmpty(c.position)}`}
        onClick={() => setOpen((v) => !v)}
      >
        <DataTableCell>{orEmpty(c.departmentName)}</DataTableCell>
        <DataTableCell>{orEmpty(c.actualAddress)}</DataTableCell>
        <DataTableCell className="font-medium text-txt">{orEmpty(c.position)}</DataTableCell>
        <DataTableCell>{orEmpty(p.fullName)}</DataTableCell>
        <DataTableCell className="whitespace-nowrap">
          {p.snils ? formatSnils(p.snils) : EMPTY}
        </DataTableCell>
      </DataTableRow>
      {open ? (
        <tr>
          <DataTableCell colSpan={5} className="bg-header">
            <LineDetail line={line} />
          </DataTableCell>
        </tr>
      ) : null}
    </>
  )
}

function LinesTab({
  anketa,
  orderId,
  orderNumber,
}: {
  anketa: SoutAnketa
  orderId: string
  orderNumber: string
}) {
  return (
    <div>
      <WorkplacesExportButton
        orderId={orderId}
        orderNumber={orderNumber}
        disabled={anketa.lines.length === 0}
      />
      {anketa.lines.length === 0 ? (
        <p className="py-2 text-txt-secondary">Рабочие места не заполнены.</p>
      ) : (
        <div className="overflow-x-auto">
          <DataTable>
            <DataTableHead>
              <tr>
                <DataTableHeaderCell>Подразделение</DataTableHeaderCell>
                <DataTableHeaderCell>Адрес</DataTableHeaderCell>
                <DataTableHeaderCell>Должность</DataTableHeaderCell>
                <DataTableHeaderCell>ФИО</DataTableHeaderCell>
                <DataTableHeaderCell>СНИЛС</DataTableHeaderCell>
              </tr>
            </DataTableHead>
            <DataTableBody>
              {anketa.lines.map((line, i) => (
                <LineTableRow key={line.id} line={line} index={i + 1} />
              ))}
            </DataTableBody>
          </DataTable>
        </div>
      )}
    </div>
  )
}

// --- Компонент ---

export interface SoutAnketaViewProps {
  anketa: SoutAnketa
  /** Заказ, чью анкету показываем — нужен выгрузке рабочих мест (centrika-docs!89). */
  orderId: string
  /** Номер заказа («СОУТ-12») — часть имени файла выгрузки. */
  orderNumber: string
}

/** Полный read-only просмотр анкеты СОУТ клиента — 5 вкладок по разделам формы заказа. */
export function SoutAnketaView({ anketa, orderId, orderNumber }: SoutAnketaViewProps) {
  return (
    <Tabs defaultValue="org">
      <TabsList aria-label="Разделы анкеты СОУТ" className="flex-wrap">
        <TabsTrigger value="org">Данные о компании</TabsTrigger>
        <TabsTrigger value="commission">Комиссия</TabsTrigger>
        <TabsTrigger value="responsible">Ответственный</TabsTrigger>
        <TabsTrigger value="pd">Согласие на обработку персональных данных</TabsTrigger>
        <TabsTrigger value="lines">Рабочие места</TabsTrigger>
      </TabsList>
      <TabsContent value="org">
        <OrganizationTab anketa={anketa} />
      </TabsContent>
      <TabsContent value="commission">
        <CommissionTab anketa={anketa} />
      </TabsContent>
      <TabsContent value="responsible">
        <ResponsibleTab anketa={anketa} />
      </TabsContent>
      <TabsContent value="pd">
        <PdConsentTab anketa={anketa} />
      </TabsContent>
      <TabsContent value="lines">
        <LinesTab anketa={anketa} orderId={orderId} orderNumber={orderNumber} />
      </TabsContent>
    </Tabs>
  )
}
